// =============================================================================
// SYSTEM PROMPT TEMPLATE v1
// Locked May 16, 2026
// Validated against: payment_failure_cluster (Prime Mover workspace)
// Rules enforced:
//   1. Title preservation, never strip numbers/percentages/dollars from default
//   2. Persona = affiliate marketer (no customer-facing / vendor-facing actions)
//   3. Voice = sharp colleague brief (no "big deal" / "jump on this" / pep talk)
// When tuning for new anomaly types or for the digest layer (Prompt 5),
// preserve these three rules. Edit ANOMALY_EXPLANATION_SYSTEM_PROMPT below
// and re-validate against at least one real anomaly before shipping.
// =============================================================================
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { generateText, Output } from "ai";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { getLovableModel } from "./ai-gateway";
import {
  checkBudget,
  contributePattern,
  findActivePattern,
  getCachedDecision,
  hashCacheKey,
  putCachedDecision,
  recordAiUsage,
} from "./ai-infra.server";

const CANONICAL_FIELDS = [
  "email",
  "contact_name",
  "first_name",
  "last_name",
  "phone",
  "product",
  "status",
  "tier",
  "commission_amount",
  "date_created",
  "date_status_changed",
  "ignore",
] as const;

const MapInput = z.object({
  account_id: z.string().uuid(),
  workspace_id: z.string().uuid().optional().nullable(),
  headers: z.array(z.string()).min(1).max(200),
  sample_rows: z.array(z.record(z.string(), z.any())).max(10),
  template_hint_json: z.record(z.string(), z.any()).optional(),
});

const MapSchema = z.object({
  mappings: z.array(
    z.object({
      source_column: z.string(),
      canonical_field: z.enum(CANONICAL_FIELDS),
      confidence: z.number().min(0).max(1),
      notes: z.string().optional().nullable(),
    }),
  ),
  status_value_map: z
    .array(
      z.object({
        raw_value: z.string(),
        canonical: z.enum([
          "active_trial",
          "active_paid",
          "churned_trial",
          "cancelled_paid",
          "active",
          "cancelled",
          "unknown",
        ]),
      }),
    )
    .default([]),
  detected_tiers: z
    .array(
      z.object({
        name: z.string(),
        commission_amount: z.number().nullable().optional(),
        likely_second_tier_amount: z.number().nullable().optional(),
      }),
    )
    .default([]),
});

export const aiMapCsvColumns = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => MapInput.parse(d))
  .handler(async ({ data, context }) => {
    // 1. Verify account membership
    const { data: member } = await supabaseAdmin
      .from("account_members")
      .select("user_id")
      .eq("account_id", data.account_id)
      .eq("user_id", context.userId)
      .maybeSingle();
    if (!member) {
      return { result: null, error: "Forbidden: not a member of this account", budget_blocked: false, source: "error" as const };
    }

    const modelName = "google/gemini-3-flash-preview";
    const sortedHeaders = [...data.headers].map((h) => h.trim().toLowerCase()).sort();
    const patternKey = hashCacheKey(["csv_mapping_v1", sortedHeaders]);
    const cacheKey = hashCacheKey([
      "csv_mapping_v1",
      sortedHeaders,
      data.template_hint_json ?? {},
    ]);

    // 2. Per-account decision cache (instant, free)
    const cached = await getCachedDecision<z.infer<typeof MapSchema>>(
      "csv_mapping",
      cacheKey,
    );
    if (cached) {
      await recordAiUsage({
        accountId: data.account_id,
        capability: "csv_mapping",
        model: modelName,
        cacheHit: true,
      });
      return { result: cached, error: null as string | null, budget_blocked: false, source: "cache" as const };
    }

    // 3. Cross-account pattern (learned from other accounts uploading same export format)
    const pattern = await findActivePattern("csv_mapping", patternKey);
    if (pattern) {
      const value = pattern.pattern_value as z.infer<typeof MapSchema>;
      await putCachedDecision("csv_mapping", cacheKey, value, modelName);
      await recordAiUsage({
        accountId: data.account_id,
        capability: "csv_mapping",
        model: modelName,
        cacheHit: true,
      });
      return { result: value, error: null, budget_blocked: false, source: "pattern" as const };
    }

    // 4. Budget gate before hitting the model
    const budget = await checkBudget(data.account_id, "csv_mapping");
    if (!budget.allowed) {
      return {
        result: null,
        error: budget.reason ?? "AI budget exceeded",
        budget_blocked: true,
        source: "blocked" as const,
      };
    }

    // 5. Call the model
    const model = getLovableModel(modelName);
    const prompt = [
      "You are mapping affiliate dashboard CSV columns to a canonical schema for the Super Affiliate OS.",
      `Canonical fields: ${CANONICAL_FIELDS.join(", ")}.`,
      "For each source column, choose the best canonical field. Use 'ignore' if it does not match any.",
      "From the status column values, propose a mapping to {active_trial, active_paid, churned_trial, cancelled_paid, active, cancelled, unknown}.",
      "From tier+commission columns, list distinct tiers and the typical commission_amount, plus a likely second_tier_amount if you see a clearly smaller paired value for the same tier.",
      data.template_hint_json ? `Template hints: ${JSON.stringify(data.template_hint_json)}` : "",
      `Headers: ${JSON.stringify(data.headers)}`,
      `Sample rows: ${JSON.stringify(data.sample_rows.slice(0, 5))}`,
    ]
      .filter(Boolean)
      .join("\n\n");

    try {
      const { experimental_output, usage } = await generateText({
        model,
        prompt,
        experimental_output: Output.object({ schema: MapSchema }),
      });
      // 6. Record usage
      await recordAiUsage({
        accountId: data.account_id,
        capability: "csv_mapping",
        model: modelName,
        inputTokens: usage?.inputTokens ?? 0,
        outputTokens: usage?.outputTokens ?? 0,
      });
      // 7. Persist decision in cache + contribute to cross-account pattern
      await putCachedDecision("csv_mapping", cacheKey, experimental_output, modelName);
      await contributePattern({
        accountId: data.account_id,
        workspaceId: data.workspace_id ?? null,
        patternType: "csv_mapping",
        patternKey,
        patternValue: experimental_output,
        source: "ai_first_seen",
      });
      return { result: experimental_output, error: null as string | null, budget_blocked: false, source: "ai" as const };
    } catch (err: any) {
      return {
        result: null,
        error: err?.message ?? "AI mapping failed",
        budget_blocked: false,
        source: "error" as const,
      };
    }
  });

const PasteInput = z.object({
  raw_text: z.string().min(1).max(50000),
  hint_format: z.string().optional(),
});

const PasteSchema = z.object({
  rows: z
    .array(
      z.object({
        email: z.string().nullable(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        phone: z.string().nullable(),
      }),
    )
    .max(500),
  detected_format: z.string(),
});

export const aiInterpretPaste = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => PasteInput.parse(d))
  .handler(async ({ data }) => {
    const model = getLovableModel("google/gemini-3-flash-preview");
    const prompt = [
      "Parse rows pasted from an affiliate dashboard. Each line is one contact.",
      "Extract email, first_name, last_name, phone where present. Return null when not found.",
      data.hint_format ? `Format hint: ${data.hint_format}` : "",
      "Raw text:",
      data.raw_text,
    ]
      .filter(Boolean)
      .join("\n\n");

    try {
      const { experimental_output } = await generateText({
        model,
        prompt,
        experimental_output: Output.object({ schema: PasteSchema }),
      });
      return { result: experimental_output, error: null as string | null };
    } catch (err: any) {
      return { result: null, error: err?.message ?? "AI parse failed" };
    }
  });

const ExplainInput = z.object({
  anomaly_id: z.string().uuid().optional(),
  anomaly_type: z.string(),
  default_title: z.string(),
  severity: z.string().optional(),
  workspace_name: z.string().optional(),
  detection_payload: z.record(z.string(), z.any()),
  // Back-compat: callers that previously passed { context } still work.
  context: z.record(z.string(), z.any()).optional(),
});

const ExplainSchema = z.object({
  title: z.string().min(1).max(200),
  explanation_body: z.string().min(1).max(2000),
  recommended_actions: z.array(z.string().min(1).max(300)).min(1).max(5),
});

export const ANOMALY_EXPLANATION_SYSTEM_PROMPT = `You write anomaly briefings for the Super Affiliate OS. You receive a detected anomaly with a data-driven default title and a raw detection_payload. You return JSON with: title, explanation_body, recommended_actions.

USER PERSONA, CRITICAL:
The user is an AFFILIATE MARKETER, not the company selling the product.
- They earn commissions on sales they referred to a third-party offer.
- They do NOT own the customer relationship.
- They CANNOT directly contact end customers about their billing.
- They CANNOT contact the product company's support on behalf of those customers.
- They have NO authority over dunning, refunds, or customer success.
- They MAY have those customers on their own email list (separate from the product's CRM).

When recommending actions, only suggest things an affiliate can actually execute:
- Reviewing patterns in their referrals (which campaign/source drove this cohort?)
- Adjusting their funnel or messaging based on what's failing
- Reaching out to people on THEIR list (not the product's list)
- Tracking recovery rates to learn from
- Pausing spend on underperforming sources
- Updating their bonus stack or pitch if expectations are misaligned

Never recommend: "contact [product] support", "email these customers", "review your dunning process", or anything else that assumes ownership of the customer relationship.

TITLE RULE, STRICT:
The title field must preserve or enhance the data-driven default title. Never:
- Shorten it
- Remove numbers, percentages, or dollar amounts
- Convert specifics to vague phrasing
You may improve the title by adding a sharp framing or implication. You may NEVER make it more generic. If unsure, return the default title verbatim.

VOICE RULES, STRICT:
NEVER use these phrases or anything similar:
- "This is a big deal"
- "Jump on this"
- "It's crucial that you..."
- "Don't miss this opportunity"
- "Act now"
- Any motivational filler, exclamation marks, or pep-talk

Write like a sharp colleague briefing you over coffee. Direct, specific, numerical. Lead with the data. Skip the pep talk. Trust the reader to know it matters.

Bad: "These failed payments put 24.5% of your recurring commissions at risk. You need to jump on this to prevent churn and recover that revenue."
Good: "That's a quarter of your active recurring base. If even half don't recover, you're looking at meaningful MRR loss next month, worth checking if these came from one referral source."

explanation_body: 2-3 short paragraphs. Lead with what the numbers mean for THIS affiliate's commissions. No filler.
recommended_actions: 2-4 concrete, affiliate-executable actions. No customer-facing or product-company actions.`;

export const aiAnomalyExplanation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ExplainInput.parse(d))
  .handler(async ({ data }) => {
    const model = getLovableModel("google/gemini-3-flash-preview");
    const payload = data.detection_payload ?? data.context ?? {};
    const defaultTitle = data.default_title || `Detected ${data.anomaly_type}.`;
    const prompt = [
      `Anomaly type: ${data.anomaly_type}`,
      data.severity ? `Severity: ${data.severity}` : "",
      data.workspace_name ? `Workspace (referral program): ${data.workspace_name}` : "",
      `Default title (data-driven, preserve all numbers): ${defaultTitle}`,
      `Detection payload: ${JSON.stringify(payload)}`,
      "Return JSON matching the schema. Preserve every number in the title.",
    ].filter(Boolean).join("\n\n");

    try {
      const { experimental_output, usage } = await generateText({
        model,
        system: ANOMALY_EXPLANATION_SYSTEM_PROMPT,
        prompt,
        experimental_output: Output.object({ schema: ExplainSchema }),
      });
      const out = {
        title: experimental_output.title,
        explanation_body: experimental_output.explanation_body,
        recommended_actions: experimental_output.recommended_actions,
      };
      if (data.anomaly_id) {
        await supabaseAdmin
          .from("anomalies")
          .update({
            title: out.title,
            explanation_body: out.explanation_body,
            recommended_actions: out.recommended_actions,
            ai_explanation: out.explanation_body,
            ai_generated_at: new Date().toISOString(),
          })
          .eq("id", data.anomaly_id);
      }
      return {
        ...out,
        usage: usage ?? null,
        explanation: out.explanation_body,
        error: null as string | null,
      };
    } catch (err: any) {
      return {
        title: defaultTitle,
        explanation_body: `Detected ${data.anomaly_type}.`,
        recommended_actions: ["Review the affected contacts in this workspace."],
        usage: null,
        explanation: `Detected ${data.anomaly_type}.`,
        error: err?.message ?? "AI explanation failed",
      };
    }
  });
